import { Filter, Operator, PaginatedTanstackQueryParams, Sort } from '@/lib/types/v1'
import { useUserStore } from '@/stores/user'
import { computed, ComputedRef, Ref, ref, toValue, watchEffect } from 'vue'
import { storeToRefs } from 'pinia'
import dayjs, { isDayjs } from 'dayjs'
import httpClient from '@/utils/httpClient'
import { Entity, isArray, isBoolean, isNumber, isString } from '@/lib/utils'
import { useRoute } from '@/composables/route'
import { CompanySecretRouteName } from '@/views/company/secret/routes'
import { CompanyAssetRouteName } from '@/views/company/asset/routes'
import { CompanyLogRouteName } from '@/views/company/log/routes'

export function slugify(text: string) {
    return text
        ?.toString() // Cast to string (optional)
        .normalize('NFKD') // The normalize() using NFKD method returns the Unicode Normalization Form of a given string.
        .toLowerCase() // Convert the string to lowercase letters
        .trim() // Remove whitespace from both sides of a string (optional)
        .replace(/\s+/g, '-') // Replace spaces with -
        .replace(/[^\w-]+/g, '') // Remove all non-word chars
        .replace(/_/g, '-') // Replace _ with -
        .replace(/--+/g, '-') // Replace multiple - with single -
        .replace(/-$/g, '') // Remove trailing -
}

export const useOperatorOptions = (filter?: string[]): Ref<Operator[]> => {
    const options = ref([
        { label: 'Equal to', value: '=' },
        { label: 'Not equal to', value: '!=' },
        { label: 'Greater than', value: '>' },
        { label: 'Less than', value: '<' },
        { label: 'Greater than or equal to', value: '>=' },
        { label: 'Less than or equal to', value: '<=' },
        { label: 'Like', value: 'like' },
        { label: 'Not like', value: 'not like' },
    ]) as Ref<Operator[]>

    return ref(
        filter ? options.value.filter((option) => filter.includes(option.value)) : options.value
    )
}

export enum QueryKeys {
    Accountings = 'accountings',
    Articles = 'articles',
    BankFeeds = 'bank-feeds',
    BankFeedHistory = 'bank-feed-history',
    BankFeedImportParams = 'bank-feed-import-params',
    BankFeedSources = 'bank-feed-sources',
    BillingExcluded = 'billing-excluded',
    Collectors = 'collectors',
    Contacts = 'contacts',
    ContactRoles = 'contact-roles',
    Companies = 'companies',
    CompanyAssets = 'company-assets',
    CompanyServices = 'company-services',
    Conversations = 'conversations',
    Categories = 'categories',
    CustomerSiteTypes = 'customer-site-types',
    Customers = 'customers',
    CustomerTypes = 'customer-types',
    Countries = 'countries',
    Cloudflares = 'cloudflares',
    CloudflareAccounts = 'cloudflare-accounts',
    CloudflareZones = 'cloudflare-zones',
    CloudflareZoneRecords = 'cloudflare-zone-records',
    CpanelAccounts = 'cpanel-accounts',
    CpanelEmailAccounts = 'cpanel-email-accounts',
    CpanelAddonDomains = 'cpanel-addon-domains',
    Datums = 'datums',
    DatumDimensions = 'datum-dimensions',
    DocumentTemplates = 'document-templates',
    ExpiredCards = 'expired-cards',
    ExpiringDomains = 'expiring-domains',
    EmailTemplates = 'email-templates',
    Forms = 'forms',
    FNN = 'fnn',
    Groups = 'groups',
    Invoices = 'invoices',
    InvoiceItems = 'invoice-items',
    Icons = 'icons',
    IPLists = 'ip-lists',
    Logs = 'logs',
    Locations = 'locations',
    Links = 'links',
    Mailboxes = 'mailboxes',
    NotificationChannels = 'notification-channels',
    NotificationChannel = 'notification-channel',
    NotificationTypes = 'notification-types',
    NotificationType = 'notification-type',
    Paginated = 'paginated',
    Payments = 'payments',
    Products = 'products',
    Rates = 'rates',
    RadiusUsers = 'radius-users',
    RadiusLoginHistory = 'radius-login-history',
    Receivables = 'receivables',
    Receipts = 'receipts',
    Regexes = 'regexes',
    Regions = 'regions',
    RSS = 'rss',
    CCPayments = 'cc-payments',
    SentEmails = 'sent-emails',
    Services = 'services',
    Secrets = 'secrets',
    SMSGlobal = 'sms-global',
    SMS = 'sms',
    Staff = 'staff',
    Staffs = 'staffs',
    StaffAndContacts = 'staff-and-contacts',
    Statements = 'statements',
    Tags = 'tags',
    Tariffs = 'tariffs',
    Tickets = 'tickets',
    Ticket = 'ticket',
    TicketTypes = 'ticket-types',
    TicketTemplates = 'ticket-templates',
    TicketPriorities = 'ticket-priorities',
    TicketDepartments = 'ticket-departments',
    Tenants = 'tenants',
    User = 'user',
    UserNotifications = 'user-notifications',
    VocusNBN = 'vocus-nbn',
    VocusNBNServices = 'vocus-nbn.services',
    VocusNBNInvoices = 'vocus-nbn-invoices',
    VocusMobiles = 'vocus-mobiles',
    VocusMobileServers = 'vocus-mobile-servers',
    VocusMobileUsages = 'vocus-mobile-usages',
}

export type QueryParams = {
    page?: Ref<number | undefined>
    limit?: Ref<number | undefined>
    sort?: Ref<Sort[] | undefined>
    filter?: Ref<Filter[] | undefined> | ComputedRef<Filter[] | undefined>
    q?: Ref<string | undefined>
    view?: Ref<string | undefined>
    groupBy?: Ref<string | undefined>
    enabled?: Ref<boolean | undefined>
    staleTime?: Ref<number | undefined>
}

export const useListQueryParams = (params?: QueryParams) => {
    const page = ref(1)
    const limit = ref(20)
    const sort = ref<Sort[] | undefined>()
    const filter = ref<Filter[] | undefined>()
    const q = ref()
    const view = ref('flat')
    const enabled = ref(true)
    const staleTime = ref(1000 * 60 * 5)
    const groupBy = ref()

    watchEffect(() => {
        const value = toValue(params)

        if (value?.page?.value) {
            page.value = value.page.value
        }

        if (value?.limit?.value) {
            limit.value = value.limit.value
        }

        if (value?.enabled?.value) {
            enabled.value = value.enabled.value
        }

        if (value?.staleTime?.value) {
            staleTime.value = value.staleTime.value
        }

        if (value?.view?.value) {
            view.value = value.view.value
        }

        sort.value = value?.sort?.value
        filter.value = value?.filter?.value
        q.value = value?.q?.value
        groupBy.value = value?.groupBy?.value
    })

    const key = computed(() => {
        return [
            `q:${q.value}`,
            `page:${page.value}`,
            `limit:${limit.value}`,
            `sort:${JSON.stringify(sort.value)}`,
            `filter:${JSON.stringify(filter.value)}`,
            `view:${view.value}`,
            `group-by:${groupBy.value}`,
        ].join(':')
    })

    return {
        key,
        page,
        limit,
        sort,
        filter,
        q,
        view,
        enabled,
        staleTime,
        groupBy,
    }
}

export const usePaginatedTanstackQueryParams = (
    params?: PaginatedTanstackQueryParams | (() => PaginatedTanstackQueryParams),
    defaultParams?: PaginatedTanstackQueryParams
) => {
    const page = ref(1)
    const limit = ref(defaultParams?.limit ?? 20)
    const sort = ref<Sort[] | undefined>()
    const filter = ref<Filter[]>([])
    const q = ref()
    const view = ref('flat')
    const enabled = ref(true)
    const staleTime = ref(0)
    const groupBy = ref()
    const key = ref<string | undefined>()
    const refetchOnWindowFocus = ref(true)
    const route = useRoute()
    const fields = ref<string | null>(null)

    watchEffect(() => {
        const value: PaginatedTanstackQueryParams | undefined | null = toValue(params)

        if (isNumber(value?.page)) {
            page.value = value.page
        }

        if (isNumber(value?.limit)) {
            limit.value = value.limit
        }

        if (isBoolean(value?.enabled)) {
            enabled.value = value.enabled
        }

        if (isNumber(value?.staleTime)) {
            staleTime.value = value.staleTime
        }

        if (isString(value?.view)) {
            view.value = value.view
        }

        if (isString(value?.fields)) {
            fields.value = value?.fields
        }

        if (isString(value?.key)) {
            key.value = value.key
        }

        if (isBoolean(value?.refetchOnWindowFocus)) {
            refetchOnWindowFocus.value = value?.refetchOnWindowFocus
        }

        if (isArray(value?.filter)) {
            filter.value = JSON.parse(JSON.stringify(value.filter))
        }

        if (isString(value?.companyUuid)) {
            filter.value.push({
                column: 'company_uuid',
                operator: '=',
                value: value.companyUuid,
            })
        }

        if (isString(value?.companyUuid) && route.name.value === CompanySecretRouteName.Index) {
            filter.value.push({
                column: 'owner_type',
                operator: '=',
                value: Entity.Company,
            })
            filter.value.push({
                column: 'owner_uuid',
                operator: '=',
                value: value.companyUuid,
            })
        }

        if (isString(value?.companyUuid) && route.name.value === CompanyLogRouteName.Index) {
            filter.value.push({
                column: 'group_type',
                operator: '=',
                value: Entity.Company,
            })
            filter.value.push({
                column: 'group_uuid',
                operator: '=',
                value: value.companyUuid,
            })
        }

        if (route.name.value === CompanyAssetRouteName.Secrets) {
            filter.value.push({
                column: 'owner_uuid',
                operator: '=',
                value: route.companyAssetUuid.value,
            })

            filter.value.push({
                column: 'owner_type',
                operator: '=',
                value: 'company_asset',
            })
        }

        sort.value = value?.sort
        q.value = value?.q
        groupBy.value = value?.groupBy
    })

    const queryKey = computed(() => {
        return [
            `q:${q.value}`,
            `page:${page.value}`,
            `limit:${limit.value}`,
            `sort:${JSON.stringify(sort.value)}`,
            `filter:${JSON.stringify(filter.value)}`,
            `view:${view.value}`,
            `group-by:${groupBy.value}`,
            `fields:${fields.value}`,
        ].join(':')
    })

    return {
        key,
        page,
        limit,
        sort,
        filter,
        q,
        queryKey,
        view,
        enabled,
        staleTime,
        groupBy,
        refetchOnWindowFocus,
        fields,
    }
}

export const parseDate = (date: Date | string = '', withTime = true) => {
    const userStore = useUserStore()
    const { getUserLocaleKey } = storeToRefs(userStore)
    const locale = getUserLocaleKey.value || 'en-US'
    const raw = date
    let temp = raw ? dayjs(raw) : raw

    if (isDayjs(temp)) {
        const format = withTime ? 'L LT' : 'L'
        temp = temp.locale(locale)

        return temp.format(format)
    }

    return temp
}

export const formatSearchFilters = (columns: string[], searchValue: string) => {
    return columns.flatMap((column, index) => {
        const condition = [
            column,
            'contains',
            searchValue,
            {
                columnIndex: index,
                filterValue: searchValue,
                selectedFilterOperation: '',
            },
        ]
        return index === 0 ? [condition] : ['or', condition]
    })
}

export const formatFetchQuery = (query: any, params: any) => {
    return Object.keys(Object.assign(params, query))
        .filter((key) => params[key])
        .reduce((obj, key) => {
            obj[key] = params[key]
            return obj
        }, {})
}

export const formatPaginationData = (data: any) => {
    const per_page = parsePaginationParam(Number(data.perPage), 100)
    const current_page = parsePaginationParam(Number(data.currentPage), 1)
    const total = data.total

    return {
        total: total,
        per_page: per_page,
        current_page: current_page,
        last_page: Math.ceil(total / 50),
        from: (current_page - 1) * per_page + 1,
        to: Math.min(current_page * per_page, total),
    }
}

export const formatFilter = (columns, value) => {
    const conditions = columns.map((col) => [col, 'contains', value])
    return conditions.flatMap((cond, index) => (index === 0 ? [cond] : ['or', cond]))
}

export const formatSort = (sortArray) => {
    return sortArray.map(({ column, direction }) => ({
        selector: column,
        desc: direction === 'desc',
    }))
}

export const parsePaginationParam = (param: unknown, defaultValue: number) => {
    const num = Number(param)
    return Number.isFinite(num) && num > 0 ? num : defaultValue
}

export const exportBlobCsv = async (url: string, params: any, filenamePrefix = 'Export') => {
    const { data } = await httpClient.post(url, params, { responseType: 'blob' })

    const href = window.URL.createObjectURL(data)

    const anchor = document.createElement('a')
    const date = dayjs().format('YYYY.MM.DD hh.mm A')

    anchor.href = href
    anchor.download = `${filenamePrefix} ${date}.csv`

    document.body.appendChild(anchor)
    anchor.click()

    document.body.removeChild(anchor)
    window.URL.revokeObjectURL(href)
}

export const getDataByPath = (obj, path) => {
    return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : undefined
    }, obj)
}
